import { useCallback, useEffect, useState } from 'react';

import differenceInSeconds from 'date-fns/differenceInSeconds';

import { useSynchronizedInterval } from 'src/hooks/useSynchronizedInterval';
import { millisecondsToHHMMSS } from 'src/utils/date';

const useRemainingTimer = (endDate: Date) => {
  const getRemainingSeconds = useCallback(() => {
    return Math.max(differenceInSeconds(endDate, Date.now(), { roundingMethod: 'ceil' }), 0);
  }, [endDate]);

  const [remainingSeconds, setRemainingSeconds] = useState(getRemainingSeconds);
  const [isTimerOver, setIsTimeOver] = useState(() => getRemainingSeconds() === 0);

  const { start: startInterval, stop } = useSynchronizedInterval(() => {
    const updatedRemainingSeconds = getRemainingSeconds();

    if (updatedRemainingSeconds === 0) {
      setIsTimeOver(true);
    }

    setRemainingSeconds(updatedRemainingSeconds);
  });

  const start = useCallback(() => {
    if (getRemainingSeconds() === 0) {
      setIsTimeOver(true);
      return;
    }
    startInterval();
  }, [startInterval, getRemainingSeconds]);

  useEffect(() => {
    if (isTimerOver) {
      stop();
    }
  }, [isTimerOver, stop]);

  return {
    start,
    stop,
    remainingTime: {
      seconds: remainingSeconds,
      text: millisecondsToHHMMSS(remainingSeconds * 1000),
    },
    isTimerOver,
  };
};

export default useRemainingTimer;
